<!--
  The record list beside the canvas.

  Why it exists: moving between inscriptions used to mean going back to the work queue and
  clicking in again. `annotator-api-frontend.md` measures every decision here against letters
  boxed per minute, and a round trip through the queue per stone was the largest remaining tax
  on that.

  What it lists: `queue.items` — the *same* ordered page the task queue renders, read through
  `recordNav`. It never fetches its own list. The pager in the record header steps over that same
  array, so the position readout and the visible order cannot disagree.

  **It renders no pager while it is open** *(2026-08-19)*. It used to, a few pixels left of the
  identical one in the record header, which is not a shortcut — it is two controls to read before
  pressing either. Open, this is the list; the header is the pager. Collapsed, the list is hidden
  and the rail carries the two arrows, because then the sidebar has nothing else to be.

  It used to carry a copy of the work queue's filter controls. Those went to the corpus browser
  with the rest of them: the list here is now whatever the chosen task has left, and narrowing
  it is not a thing an annotator mid-inscription should be doing.

  The list is of **inscriptions**, because that is the unit the queue, the transliteration and
  the split are all defined on. The canvas edits one *image*, and a stone can have several
  photographs; the row therefore shows the record's image count, and the pager steps within a
  record's images before it moves to the next record. See `lib/nav.ts` for why flattening to
  images was rejected.

  Colour is never the only signal: each row states its progress in words ("done", "in progress",
  "not started") and carries a filled/half/empty **shape**; hue only reinforces them.
-->
<script lang="ts">
  import { untrack } from 'svelte';

  import { blurOnPointerActivation } from '../lib/keymap';
  import {
    RECORD_PROGRESS_LABEL,
    RECORD_PROGRESS_MARK,
    displaySiglum,
    recordBoxFraction,
    recordProgressState,
  } from '../lib/nav';
  import { href } from '../lib/router.svelte';
  import { recordNav } from '../lib/stores/nav.svelte';
  import { queue } from '../lib/stores/queue.svelte';
  import RecordPager from './RecordPager.svelte';

  let listEl = $state<HTMLUListElement | null>(null);

  /**
   * Below the workspace's own breakpoint the three columns stack, and this rail stops being a
   * column at all. Same query as `Workspace.svelte`'s `@media (max-width: 1100px)`; they have
   * to agree, because the whole point is what the layout is doing.
   */
  let stacked = $state(false);
  $effect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(max-width: 1100px)');
    const sync = () => {
      stacked = query.matches;
    };
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  });

  /**
   * Collapse is a width-saving gesture, and stacked there is no width to save.
   *
   * Collapsed *and* stacked rendered as a ~160px band of empty space above the canvas holding
   * a clipped "1 of 60" and two arrows — the widest element on the screen, containing nothing,
   * pushing the photograph down. The collapsed styling is a 44px **vertical** strip and there
   * is no sensible horizontal form of it. So the preference is ignored while stacked rather
   * than given a second layout to maintain; it is remembered, and applies again at width.
   */
  const open = $derived(recordNav.sidebarOpen || stacked);
  const records = $derived(recordNav.records);
  const currentId = $derived(recordNav.currentInscriptionId);
  const task = $derived(queue.task);
  const loadingFirstPage = $derived(queue.loading && !queue.page);
  // The open record is not on the page the queue currently holds — a different page, another
  // task, or a record opened from the browser. Say so; it is also why the pager is disabled.
  const offList = $derived(currentId !== null && records.length > 0 && !records.some((r) => r.id === currentId));

  // Untracked call, same reasoning as everywhere else in this app: `queue.load()` writes the
  // fields the guard inside it reads, so a tracked call would restart itself.
  $effect(() => {
    untrack(() => recordNav.ensureLoaded());
  });

  // Keep the current row in view when it changes underneath a scrolled list. `scrollIntoView`
  // is missing in jsdom, hence the capability check rather than an optional call.
  $effect(() => {
    const id = currentId;
    const node = listEl;
    if (!open || id === null || !node) return;
    const row = node.querySelector<HTMLElement>(`[data-nav-record="${id}"]`);
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
  });

  function rowClick(event: MouseEvent, inscriptionId: number): void {
    // Let the browser have modified clicks: opening a record in a new tab risks no work in this
    // one, and the href is a real address.
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    blurOnPointerActivation(event);
    void recordNav.openRecord(inscriptionId);
  }
</script>

<aside class="recordnav" class:collapsed={!open} aria-label="Work queue records">
  <div class="navhead">
    <button
      class="subtle toggle"
      onclick={(event) => {
        recordNav.toggleSidebar();
        blurOnPointerActivation(event);
      }}
      aria-expanded={open}
      aria-controls="record-nav-list"
      aria-label={open ? 'Hide the record list' : 'Show the record list'}
      title={open ? 'Hide the record list (\\)' : 'Show the record list (\\)'}
    >
      <!-- Inline, because there is no icon library here and a font glyph can render as tofu. -->
      <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
        <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" fill="none" stroke="currentColor" />
        <line x1="6" y1="2.5" x2="6" y2="13.5" stroke="currentColor" />
        {#if open}
          <path d="M11.5 6 L9 8 L11.5 10" fill="none" stroke="currentColor" />
        {:else}
          <path d="M9 6 L11.5 8 L9 10" fill="none" stroke="currentColor" />
        {/if}
      </svg>
    </button>
    {#if open}
      <span class="title">{task?.label ?? 'Records'}</span>
      <span class="spacer"></span>
      <span class="small muted mono" title="Rows on this queue page, of all with work left">
        {records.length}/{queue.total}
      </span>
    {/if}
  </div>

  {#if open && task}
    <!--
      Say which list this is. A pager that silently walks 60 of 3,446 records is worse than one
      that walks all of them: the annotator has no way to tell whether the corpus is small or
      the view is narrow. Widening it is no longer a control here — the scope belongs to the
      task, and every other record is in the corpus browser.
    -->
    <p class="small seednote">
      <span class="seedrow">
        <svg class="seed" viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
          <path
            d="M6 11V5.6"
            fill="none"
            stroke="currentColor"
            stroke-width="1.3"
            stroke-linecap="round"
          />
          <path
            d="M6 5.6C6 3.6 4.5 2 2.4 1.8 2.2 3.9 3.8 5.5 6 5.6Z M6 5.6c0-2 1.5-3.6 3.6-3.8.2 2.1-1.4 3.7-3.6 3.8Z"
            fill="currentColor"
          />
        </svg>
        <strong>{task.label} · {task.scope_label}</strong>
      </span>
      What this task has left, in the order it hands it out.
      <a class="small" href={href({ name: 'queue', task: null })}>Switch task</a>
    </p>
  {/if}

  {#if open}
    <!--
      No pager here. `Workspace` renders the `bar` pager in the record header, above the
      photograph and beside the name of what it steps through, and a second copy of the same
      two buttons a few pixels to the left is not a shortcut — it is two controls to read
      before pressing either. The open sidebar is the list; the header is the pager. The
      collapsed rail below still carries the arrows, because when the list is hidden the
      sidebar has nothing else to be.
    -->
    {#if queue.problem}
      <div class="banner error small">
        <strong>Could not load the record list</strong>
        <p>{queue.problem.message}</p>
        <p class="muted mono">code: {queue.problem.code}</p>
      </div>
    {/if}

    {#if offList}
      <p class="banner warn small">
        The inscription on screen is not in this list — it was opened from the
        <a href={href({ name: 'browse' })}>corpus browser</a>, or this task has no work left on
        it. Open it from the <a href={href({ name: 'queue', task: queue.taskId })}>work queue</a>
        to step through from here.
      </p>
    {/if}

    <div class="listwrap">
      {#if loadingFirstPage}
        <p class="small muted pad" aria-busy="true">Loading the work queue…</p>
      {:else if records.length === 0}
        <p class="small muted pad">
          {#if !queue.taskId}
            <!-- Arrived straight on an image, by deep link or from the browser. -->
            No task is being worked, so there is no list to step through.
            <a href={href({ name: 'queue', task: null })}>Pick a task</a>.
          {:else if task?.blocked_reason}
            <!--
              Distinguish "the scope is empty" from "you have finished". A fresh database has no
              seed batch until `python -m hismaic.db.seed_sample` has run, and an unqualified
              "nothing to step through" would read as the latter.
            -->
            {task.blocked_reason}
          {:else}
            Nothing is left in this task, so there is nothing to step through.
            <a href={href({ name: 'queue', task: null })}>Pick another</a>.
          {/if}
        </p>
      {:else}
        <ul id="record-nav-list" class="rows" bind:this={listEl}>
          {#each records as record (record.id)}
            {@const state = recordProgressState(record)}
            {@const current = record.id === currentId}
            <li>
              <a
                class="reclink"
                class:current
                data-nav-record={record.id}
                aria-current={current ? 'page' : undefined}
                href={href({ name: 'inscription', inscriptionId: record.id })}
                onclick={(event) => rowClick(event, record.id)}
              >
                <span class="line1">
                  <span class="mark" aria-hidden="true">{RECORD_PROGRESS_MARK[state]}</span>
                  <span class="siglum" title={record.siglum_raw}>{displaySiglum(record)}</span>
                  {#if current}<span class="visually-hidden">(current)</span>{/if}
                  <!--
                    The state always reaches assistive tech; it is only *shown* when it is
                    news. With nothing annotated yet every record is 'not started', so
                    printing it on all 3,446 rows was noise on every line and left no visual
                    weight for the handful of rows that differ. The mark glyph carries the
                    common case.
                  -->
                  <span class="visually-hidden">{RECORD_PROGRESS_LABEL[state]}</span>
                  {#if record.seed_batch}
                    <!--
                      Inline SVG, not a glyph. A sprout character would be the obvious choice
                      and this project has already been bitten by font tofu in the chip strip;
                      a path cannot fail to render. `aria-hidden` with the words beside it, so
                      the marker is never the only carrier.
                    -->
                    <svg
                      class="seed"
                      viewBox="0 0 12 12"
                      width="11"
                      height="11"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M6 11V5.6"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.3"
                        stroke-linecap="round"
                      />
                      <path
                        d="M6 5.6C6 3.6 4.5 2 2.4 1.8 2.2 3.9 3.8 5.5 6 5.6Z M6 5.6c0-2 1.5-3.6 3.6-3.8.2 2.1-1.4 3.7-3.6 3.8Z"
                        fill="currentColor"
                      />
                    </svg>
                    <span class="visually-hidden">seed batch</span>
                  {/if}
                  {#if state !== 'not-started'}
                    <span class="statusword small" aria-hidden="true"
                      >{RECORD_PROGRESS_LABEL[state]}</span
                    >
                  {/if}
                </span>
                <!--
                  `· N boxes` only once there are any. Every record read `0 boxes` before a
                  single one was drawn, which is 3,446 rows of a constant — and a constant
                  carries no information while still costing a line's worth of attention.
                -->
                <span class="line2 small muted">
                  {record.token_count} letters · {record.images_done}/{record.image_count} img{#if record.annotation_count > 0}
                    · {record.annotation_count} boxes{/if}
                </span>
                <!-- Decorative: the numbers above already carry the figure in text. -->
                <span class="bar" aria-hidden="true">
                  <span class="fill" style="width:{Math.round(recordBoxFraction(record) * 100)}%"
                  ></span>
                </span>
                {#if record.queue_block_reasons.length}
                  <span class="line2 small warnword">
                    blocked: {record.queue_block_reasons.join(', ')}
                  </span>
                {/if}
              </a>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {:else}
    <div class="rail">
      <RecordPager variant="rail" />
    </div>
  {/if}
</aside>

<style>
  .recordnav {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: var(--bg-panel);
    padding: 8px;
    overflow: hidden;
  }

  .recordnav.collapsed {
    padding: 8px 4px;
    align-items: center;
  }

  .navhead {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: none;
  }

  .title {
    font-weight: 600;
    font-size: 0.85rem;
  }

  .toggle {
    display: inline-flex;
    align-items: center;
    padding: 3px;
  }

  .listwrap {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    /*
      Never horizontally. A siglum like `AMJ 003 (Jobling 1982: 469 )` is far wider than a
      264px rail, and without this the list scrolled sideways — clipping the leading
      characters of every row (`AMJ 003` reading as `MJ 003`) and adding a scrollbar that
      stole vertical space. The row content ellipsizes instead; see `.siglum` / `.line2`.
    */
    overflow-x: hidden;
    scrollbar-width: thin;
  }

  .pad {
    padding: 8px 4px;
    margin: 0;
  }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
    /*
      `min-width: auto` is the default for a flex/grid item, and it refuses to shrink below
      the intrinsic width of its content. That is what defeated the ellipsis rules below —
      they were correct, but the boxes they applied to had already been widened to fit the
      longest siglum. The `min-width: 0` chain here, on `.reclink` and on `.siglum` is what lets
      the text actually truncate.
    */
    min-width: 0;
  }

  /*
    Named `reclink`, NOT `row`.

    `app.css` defines `.row` as a global layout utility (`display:flex; align-items:center;
    gap:8px`). This element used to carry that class name too, and Svelte's scoped rule set
    `flex-direction: column` without setting `align-items` — so the global `center` still
    applied. In a column flex that centres each child horizontally, which is why `.line1`
    (a content-sized flex row) sat indented while `.line2` (block, full width) stayed flush
    left, and no two lines in the list agreed on a left edge. `align-items` below is now
    explicit as well, so a future global cannot reach in again.
  */
  .reclink {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1px;
    padding: 4px 8px 5px;
    border-left: 3px solid transparent;
    border-radius: 0 4px 4px 0;
    text-decoration: none;
    color: var(--text);
    min-width: 0;
  }

  .reclink:hover {
    background: var(--bg-raised);
  }

  .reclink.current {
    border-left-color: var(--accent);
    background: var(--bg-raised);
    font-weight: 600;
  }

  .line1 {
    display: flex;
    align-items: baseline;
    gap: 5px;
    min-width: 0;
  }


  .siglum {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* Takes the slack, and may shrink — the status word beside it is `nowrap`. */
    flex: 1 1 auto;
    min-width: 0;
  }

  .seed {
    flex: none;
    color: var(--accent);
    /* Sits on the text baseline rather than floating above it in a baseline-aligned row. */
    align-self: center;
  }

  .seednote {
    flex: none;
    margin: 0;
    padding: 6px 8px;
    border: 1px solid var(--border);
    border-left: 3px solid var(--accent);
    border-radius: 0 4px 4px 0;
    background: var(--bg-raised);
    color: var(--text-dim);
  }

  .seedrow {
    display: flex;
    align-items: center;
    gap: 5px;
    color: var(--text);
  }

  /* Fixed column so every siglum starts on the same left edge, whatever the glyph. */
  .mark {
    font-size: var(--text-xs);
    color: var(--text-dim);
    flex: none;
    width: 0.9em;
    text-align: center;
  }

  .statusword {
    color: var(--text-dim);
    white-space: nowrap;
  }

  .reclink.current .statusword {
    color: var(--accent);
  }

  .line2 {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .warnword {
    color: var(--warn);
  }

  .bar {
    display: block;
    height: 3px;
    background: var(--bg-input);
    border-radius: 2px;
    overflow: hidden;
  }

  .fill {
    display: block;
    height: 100%;
    background: var(--accent);
  }

  .rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  @media (max-width: 1100px) {
    /* Stacked above the canvas rather than beside it. Capped so a fifty-row list cannot push
       the image off the screen. */
    .recordnav {
      max-height: 38vh;
    }
  }
</style>
